import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {isDefined} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ReportElementIdsEnum} from '../../helpers/reportHelpers/reportTypes';
import {ReportSectionFrame} from '../reportElements/reportSection/reportSectionFrame';
import {AnalyticsTooltipCalculationContent} from '../reportElements/tooltips/analyticsTooltipCalculationContent';
import {AnalyticsEducationTooltip, AnalyticsTooltip} from '../reportElements/tooltips/analyticsTooltips';

interface MetricSectionProps {
  label: ReactNode;

  reportSectionId: ReportElementIdsEnum; // Used for tracking purpose.
  tooltip?: AnalyticsEducationTooltip;

  children?: ReactNode;
  className?: string;
}

const SyledReportSectionFrame = styled(ReportSectionFrame)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  align-items: center;
  overflow-x: hidden;
`;

const LabelRow = styled.div`
  position: relative;
  line-height: 19px;
`;

// Places the tooltip on the right side of the parent while removing it from the flow.
const StyledAnalyticsTooltip = styled(AnalyticsTooltip)`
  position: absolute;
  top: 50%;
  left: calc(100% + 4px);
  transform: translateY(-50%);
`;

export const MetricSection: FC<MetricSectionProps> = ({
  label,
  tooltip,
  reportSectionId,
  children,
  className,
}) => {
  const hasTooltip = isDefined(tooltip);
  const [isHovered, setIsHovered] = useState(false);

  // `AnalyticsEducationTooltip` defines `calculationExplanation` when it is actually unused by the `AnalyticsTooltip` component.
  // This puts the calculation explanation inside `tooltipContent`, as done in other places.
  // `AnalyticsTooltip` should be fixed to use `calculationExplanation`.
  const actualTooltipContent = hasTooltip && (
    <>
      {tooltip.tooltipContent}
      <AnalyticsTooltipCalculationContent content={tooltip.calculationExplanation} />
    </>
  );

  const updatedTooltip: AnalyticsEducationTooltip | undefined = tooltip && {
    ...tooltip,
    tooltipContent: actualTooltipContent,
  };

  return (
    <SyledReportSectionFrame
      className={className}
      onMouseEnter={() => hasTooltip && setIsHovered(true)}
      onMouseLeave={() => hasTooltip && setIsHovered(false)}
    >
      <LabelRow>
        {label}
        {isHovered && <StyledAnalyticsTooltip tooltip={updatedTooltip} reportSectionId={reportSectionId} />}
      </LabelRow>
      {children}
    </SyledReportSectionFrame>
  );
};
